/**
 * Letters out per day, for the last month.
 *
 * A BAR PER DAY, INCLUDING THE EMPTY ONES. A line drawn only through the days
 * that have rows joins the two sides of a ten-day pause with a straight
 * diagonal, which reads as steady sending. The empty days are the shape worth
 * seeing: a warm-up ramp, a weekend, the morning somebody pulled the stop.
 *
 * EACH BAR IS SPLIT BY OUTCOME, AND THE SPLIT IS A COHORT. The darker part is
 * how much of that day's batch has since bounced - not how many bounces arrived
 * that day. Keyed the other way the chart would be unreadable, because a bounce
 * can land days after its letter; keyed this way it answers the question a
 * sender actually has, which is whether a particular batch was any good.
 *
 * ⚠ The newest days are still filling in. A bounce takes hours and a reply takes
 * days, so the right-hand end of the chart always understates its own outcomes.
 * The panel says so rather than letting a reader conclude that yesterday's batch
 * was cleaner than last week's.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { DailyPoint } from "../../../../lib/marketingOverview";
import { Panel, num } from "../shared";
import { peakDay, windowTotal } from "./lib";

/** The strip's height in pixels. Bars are a share of it. */
const TRACK = 96;

/** What a day with no letters still draws, so the axis stays visible. A bar that
 *  disappears reads as a gap in the data rather than as a quiet day. */
const FLOOR = 2;

export function SendChart({
  daily,
  windowDays,
  timezone,
}: {
  daily: DailyPoint[];
  windowDays: number;
  timezone: string;
}) {
  const t = useT();
  const peak = peakDay(daily);
  const total = windowTotal(daily);

  return (
    <Panel
      title={t("marketing.overview.chart.title")}
      description={t("marketing.overview.chart.intro", { days: windowDays, tz: timezone })}
    >
      {daily.length === 0 ? (
        <p className="crm-sub">{t("marketing.overview.chart.unavailable")}</p>
      ) : total === 0 ? (
        <p className="crm-sub">{t("marketing.overview.chart.empty", { days: windowDays })}</p>
      ) : (
        <>
          {/* One image with one accessible name. Thirty separate labels would
              be thirty announcements of a number nobody asked for; the summary
              is what a screen reader needs, and the per-day figures stay
              available on hover for the eye. */}
          <div
            className="flex items-end gap-[3px]"
            style={{ height: TRACK }}
            role="img"
            aria-label={t("marketing.overview.chart.summary", {
              days: windowDays,
              n: num(total),
              peak: num(peak),
            })}
          >
            {daily.map((d) => {
              const height = peak > 0 ? Math.max(FLOOR, Math.round((d.sent / peak) * TRACK)) : FLOOR;
              // The bounced band is a share of THAT DAY, so it scales against
              // the day's own bar rather than against the tallest one.
              const bad = d.sent > 0 ? Math.round((d.bounced / d.sent) * height) : 0;
              return (
                <span
                  key={d.day}
                  // `1 1 0`, never `1 1 auto`: with `auto` each column is sized
                  // by its content first and thirty of them stop sharing the
                  // row evenly the moment one is a pixel wider.
                  style={{ flex: "1 1 0", minWidth: 0, height }}
                  className="flex min-w-0 flex-col justify-end overflow-hidden rounded-[2px]"
                  title={t("marketing.overview.chart.day", {
                    day: d.day,
                    n: num(d.sent),
                    bounced: num(d.bounced),
                    replied: num(d.replied),
                  })}
                >
                  {bad > 0 ? (
                    <i style={{ height: bad, background: "var(--color-warning)" }} className="block w-full" />
                  ) : null}
                  <i
                    style={{
                      height: Math.max(d.sent > 0 ? 1 : FLOOR, height - bad),
                      // A quiet day's stub is a share of the FOREGROUND rather
                      // than the sunk-surface token: `--crm-sunk` sits a
                      // hundredth of a lightness step from the card in dark
                      // mode, so the stub was invisible there - and an
                      // invisible stub is exactly the gap in the axis this
                      // chart draws them to avoid.
                      background: d.sent > 0
                        ? "var(--color-primary)"
                        : "color-mix(in oklab, var(--color-foreground) 18%, transparent)",
                    }}
                    className="block w-full"
                  />
                </span>
              );
            })}
          </div>

          <div className="crm-mut mt-2 flex items-center justify-between gap-3 text-[11px]">
            <span className="crm-tnum">{daily[0]?.day}</span>
            <span>{t("marketing.overview.chart.settling")}</span>
            <span className="crm-tnum">{daily.at(-1)?.day}</span>
          </div>

          <p className="crm-sub mt-3">
            {t("marketing.overview.chart.foot", { n: num(total), peak: num(peak), days: windowDays })}
          </p>
        </>
      )}
    </Panel>
  );
}
